/**
 * src/components/ErrorReportPopup.js
 *
 * Error reporting popup that appears when runtime errors occur.
 * Users can describe what happened before the error, plus auto-captures
 * error details (message, stack, URL, user agent).
 */

import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  X,
  AlertTriangle,
  Send,
  Loader,
  CheckCircle,
  Bug,
} from "lucide-react";
import { submitErrorReport } from "../services/feedbackService";
import { errorDev } from '../utils/logger.js';

export default function ErrorReportPopup({
  isOpen,
  onClose,
  error = null, // Error object or string
  errorInfo = null, // React errorInfo with componentStack
  user = null,
}) {
  const { t, ready } = useTranslation();
  const safeT = (key, fallback) => {
    if (!ready) return fallback;
    try { const r = t(key); return r === key ? fallback : r; } catch (err) { errorDev("Operation failed:", err);  return fallback; }
  };

  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const textRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setDescription("");
      setSuccess(false);
      setSubmitError(null);
      setTimeout(() => textRef.current?.focus(), 150);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const errorMessage =
    typeof error === "string"
      ? error
      : error?.message || "Unknown error";

  const humanMessage = String(errorMessage || "").replace(/\s+/g, " ").trim().slice(0, 180);

  const errorStack =
    typeof error === "object"
      ? (error?.stack || "") + (errorInfo?.componentStack || "")
      : "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      await submitErrorReport(user, {
        errorMessage,
        errorStack,
        userDescription: description.trim(),
        pageUrl: window.location.href,
      });
      setSuccess(true);
      setTimeout(() => onClose(), 2500);
    } catch (err) {
      setSubmitError(err.message || "Failed to submit report");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-neutral-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-red-100 dark:border-red-900/30 bg-red-50 dark:bg-red-900/10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <Bug className="w-4 h-4 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              {safeT("error.reportTitle", "Report a Problem")}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/20 text-gray-500 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {success ? (
          /* Success */
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
              {safeT("error.reportSent", "Report Sent")}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {safeT("error.reportThank", "Thank you for helping us fix this issue.")}
            </p>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            {/* Error Details */}
            <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30 rounded-xl p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-red-700 dark:text-red-400 mb-0.5">
                    {safeT("error.detected", "Error Detected")}
                  </p>
                  <p className="text-xs text-red-600 dark:text-red-300 break-words font-mono">
                    {humanMessage}
                  </p>
                </div>
              </div>
            </div>

            {/* User Description */}
            <div>
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 block">
                {safeT("error.whatHappened", "What were you doing when this happened?")}
              </label>
              <textarea
                ref={textRef}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={safeT("error.describePlaceholder", "Describe what you were doing (optional)...")}
                rows={3}
                maxLength={1000}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-900 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none transition"
              />
            </div>

            {/* Auto-captured info note */}
            <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {safeT("error.autoCapture", "Error details, page URL, and browser info will be included automatically.")}
            </p>

            {/* Submit Error */}
            {submitError && (
              <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg">
                {submitError}
              </p>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-neutral-700 hover:bg-gray-200 dark:hover:bg-neutral-600 rounded-xl transition"
              >
                {safeT("common:cancel", "Cancel")}
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-xl transition disabled:opacity-50"
              >
                {submitting ? (
                  <span className="orrith-loader orrith-loader-xs orrith-loader-on-dark" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {safeT("error.sendReport", "Send Report")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
